"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import useIsMobile from "@/hooks/use-is-mobile";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Filters } from "@/components/ui/filters";
import { useTalentSearch } from "@/components/search/talent-search-provider";
import { useOptionalTalentSearchResults } from "@/components/search/talent-search-results-provider";
import { isStateFilterActive } from "@/features/search/lib/state-filters";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import {
  SearchCriteriaPaneBody,
  SearchCriteriaPaneFooter,
  SearchCriteriaPaneProvider,
} from "@/features/search/components/criteria/criteria-pane";
import { FilterPanelPaneSwitcher, type FilterPanelPane } from "./pane-switcher";
import { RunSearchFooter } from "./run-search-footer";
import type { RunSearchButtonProps } from "@/components/search/run-search-button";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalClose,
} from "@/components/ui/modal";
import {
  ApplyPreset,
  CreatePreset,
  type PresetTypeSlug,
} from "@/features/presets";
import type { SearchFilters } from "@/lib/api/types";
import { customInstance } from "@/lib/api/client";
import { showToast } from "@/components/ui/sonner";
import { FilterBooleanNameSection } from "./section/boolean-name";
import { FilterCertificationsSection } from "./section/certifications";
import { FilterClearancesSection } from "./section/clearances";
import { FilterCompanySection } from "./section/company";
import { FilterEducationSection } from "./section/education";
import { FilterFederalSection } from "./section/federal";
import { FilterFundingSection } from "./section/funding";
import { FilterGeneralSection } from "./section/general";
import { FilterHighlightsSection } from "./section/highlights";
import { FilterIndustrySection } from "./section/industry";
import { FilterJobSection } from "./section/job";
import { FilterLanguagesSection } from "./section/languages";
import { FilterLocationsSection } from "./section/locations";
import { FilterNetworkSection, countNetworkFilter } from "./section/network";
import { FilterSkillsSection } from "./section/skills";
import { PoolCategoryGateProvider } from "./pool-category-gate";
import { SearchPoolsPicker } from "@/features/search/components/search-pools/search-pools-picker";
import { FilterSection } from "./shared";
import { Icon } from "@/components/ui/icon";
import { useHighlightEntries } from "@/features/search/lib/highlights";
import { countCertificationFilters } from "@/features/search/lib/certifications";
import { countFederalFilters } from "@/features/search/lib/federal";
import {
  countClearanceFilters,
  pruneUnknownHighlights,
  splitLegacyClearanceHighlights,
} from "@/features/search/lib/clearances";

function countDefined(...vals: unknown[]): number {
  return vals.filter(
    (v) =>
      v !== undefined &&
      v !== null &&
      v !== false &&
      v !== "" &&
      !(Array.isArray(v) && v.length === 0),
  ).length;
}

function useSectionCounts() {
  const { filters } = useTalentSearchFilters();

  return {
    general:
      countDefined(filters.min_experience_years, filters.max_experience_years) +
      (isStateFilterActive(filters.hide_viewed) ? 1 : 0) +
      (isStateFilterActive(filters.hide_reviewed) ? 1 : 0) +
      (filters.exclude_org_profiles === false ? 1 : 0),
    locations: filters.locations?.length ?? 0,
    job:
      (filters.job_titles?.length ?? 0) +
      countDefined(
        filters.job_title_levels,
        filters.job_title_roles,
        filters.time_current_role,
        filters.average_tenure_min_months,
      ),
    company:
      (filters.companies?.length ?? 0) +
      (filters.company_locations?.length ?? 0) +
      countDefined(
        filters.company_sizes,
        filters.company_tags,
        filters.company_founded_after,
      ),
    industry: filters.industries?.length ?? 0,
    funding:
      (filters.company_funding_stages?.length ?? 0) +
      (filters.revenue_ranges?.length ?? 0) +
      countDefined(filters.investors, filters.min_funding, filters.max_funding),
    skills: filters.skills?.length ?? 0,
    education:
      (filters.universities?.length ?? 0) +
      (filters.university_locations?.length ?? 0) +
      (filters.majors?.length ?? 0) +
      countDefined(
        filters.min_degree !== "none" ? filters.min_degree : undefined,
        filters.grad_year_start,
        filters.grad_year_end,
      ),
    languages: filters.languages?.length ?? 0,
    certifications: countCertificationFilters(filters),
    federal: countFederalFilters(filters) + countClearanceFilters(filters),
    booleanName:
      countDefined(filters.boolean_expression, filters.full_text_search) +
      (filters.full_names?.length ?? 0),
    network: countNetworkFilter(filters.network),
  };
}

export function FilterSectionsContent({
  onOpenPreset,
}: { onOpenPreset?: (type: PresetTypeSlug) => void } = {}) {
  const counts = useSectionCounts();
  const { filters, updateFilter, setPoolIds } = useTalentSearchFilters();
  const {
    entries: highlightEntries,
    isLoading: highlightsLoading,
    error: highlightsError,
  } = useHighlightEntries();
  const showHighlightsSection =
    highlightsLoading || !!highlightsError || highlightEntries.length > 0;
  const { highlights: selectedHighlights, legacyClearances } =
    splitLegacyClearanceHighlights(filters.highlight_types);

  useEffect(() => {
    if (highlightsLoading || highlightsError) return;
    const selected = filters.highlight_types;
    if (!selected?.length) return;

    // Legacy clearance ids are shown (and removable) under Security Clearance
    // in the Federal section.
    const next = pruneUnknownHighlights(
      selected,
      highlightEntries.map((entry) => entry.type),
    );
    if (next.length === selected.length) return;
    updateFilter("highlight_types", next.length > 0 ? next : undefined);
  }, [
    filters.highlight_types,
    highlightEntries,
    highlightsError,
    highlightsLoading,
    updateFilter,
  ]);

  return (
    <PoolCategoryGateProvider>
      {/* Below lg the desktop header (and its "Search in" picker) is hidden. */}
      {setPoolIds ? (
        <div className="flex flex-col gap-1.5 border-b border-border px-3 py-2.5 lg:hidden">
          <span className="text-xs font-medium text-contrast/60">Search in</span>
          <SearchPoolsPicker align="start" className="w-full max-w-none justify-start" />
        </div>
      ) : null}
      <FilterSection
        sectionKey="general"
        icon="IconSettingsGear2"
        label="General"
        activeCount={counts.general || undefined}
        onClear={() => {
          updateFilter("min_experience_years", undefined);
          updateFilter("max_experience_years", undefined);
          updateFilter("hide_viewed", undefined);
          updateFilter("hide_reviewed", undefined);
          updateFilter("exclude_org_profiles", undefined);
        }}
      >
        <FilterGeneralSection />
      </FilterSection>
      <FilterSection
        sectionKey="locations"
        icon="IconMapPin"
        label="Locations"
        activeCount={counts.locations || undefined}
        onClear={() => {
          updateFilter("locations", undefined);
        }}
      >
        <FilterLocationsSection onOpenPreset={onOpenPreset} />
      </FilterSection>
      <FilterSection
        sectionKey="job"
        icon="IconBag"
        label="Job"
        activeCount={counts.job || undefined}
        onClear={() => {
          updateFilter("job_titles", undefined);
          updateFilter("job_title_levels", undefined);
          updateFilter("job_title_roles", undefined);
          updateFilter("time_current_role", undefined);
          updateFilter("average_tenure_min_months", undefined);
        }}
      >
        <FilterJobSection onOpenPreset={onOpenPreset} />
      </FilterSection>
      <FilterSection
        sectionKey="company"
        icon="IconBuildings"
        label="Company"
        activeCount={counts.company || undefined}
        onClear={() => {
          updateFilter("companies", undefined);
          updateFilter("company_locations", undefined);
          updateFilter("company_sizes", undefined);
          updateFilter("company_tags", undefined);
          updateFilter("company_founded_after", undefined);
        }}
      >
        <FilterCompanySection onOpenPreset={onOpenPreset} />
      </FilterSection>
      <FilterSection
        sectionKey="industry"
        icon="IconSchool"
        label="Industry"
        activeCount={counts.industry || undefined}
        onClear={() => updateFilter("industries", undefined)}
      >
        <FilterIndustrySection />
      </FilterSection>
      <FilterSection
        sectionKey="funding"
        icon="IconMoneybag"
        label="Funding & Revenue"
        activeCount={counts.funding || undefined}
        onClear={() => {
          updateFilter("company_funding_stages", undefined);
          updateFilter("investors", undefined);
          updateFilter("min_funding", undefined);
          updateFilter("max_funding", undefined);
          updateFilter("funding_scope", undefined);
          updateFilter("revenue_ranges", undefined);
        }}
      >
        <FilterFundingSection onOpenPreset={onOpenPreset} />
      </FilterSection>
      <FilterSection
        sectionKey="skills"
        icon="IconWhiteboard"
        label="Skills or Keywords"
        activeCount={counts.skills || undefined}
        onClear={() => {
          updateFilter("skills", undefined);
        }}
      >
        <FilterSkillsSection onOpenPreset={onOpenPreset} />
      </FilterSection>
      {showHighlightsSection ? (
        <FilterSection
          sectionKey="highlights"
          icon="IconSparklesTwo2"
          label="Highlights"
          activeCount={selectedHighlights.length || undefined}
          onClear={() => {
            updateFilter(
              "highlight_types",
              legacyClearances.length > 0 ? legacyClearances : undefined,
            );
          }}
        >
          <FilterHighlightsSection />
        </FilterSection>
      ) : null}
      <FilterSection
        sectionKey="education"
        icon="IconGraduateCap"
        label="Education"
        activeCount={counts.education || undefined}
        onClear={() => {
          updateFilter("universities", undefined);
          updateFilter("university_locations", undefined);
          updateFilter("majors", undefined);
          updateFilter("grad_year_start", undefined);
          updateFilter("grad_year_end", undefined);
        }}
      >
        <FilterEducationSection onOpenPreset={onOpenPreset} />
      </FilterSection>
      <FilterSection
        sectionKey="languages"
        icon="IconTranslate"
        label="Languages"
        activeCount={counts.languages || undefined}
        onClear={() => {
          updateFilter("languages", undefined);
        }}
      >
        <FilterLanguagesSection />
      </FilterSection>
      <FilterSection
        sectionKey="certifications"
        icon="IconMedal"
        label="Certifications"
        activeCount={counts.certifications || undefined}
        onClear={() => {
          updateFilter("certification_names", undefined);
          updateFilter("certification_vendors", undefined);
          updateFilter("certification_status", undefined);
        }}
      >
        <FilterCertificationsSection />
      </FilterSection>
      <FilterSection
        sectionKey="federal"
        icon="IconBank"
        label="Federal"
        activeCount={counts.federal || undefined}
        onClear={() => {
          updateFilter("occupational_series", undefined);
          updateFilter("gs_grade_min", undefined);
          updateFilter("gs_grade_max", undefined);
          updateFilter("gs_include_equivalents", undefined);
          updateFilter("clearance_levels", undefined);
          updateFilter("clearance_strict", undefined);
          updateFilter("clearance_polygraphs", undefined);
          if (legacyClearances.length > 0) {
            updateFilter(
              "highlight_types",
              selectedHighlights.length > 0 ? selectedHighlights : undefined,
            );
          }
        }}
      >
        <FilterFederalSection />
        <FilterClearancesSection />
      </FilterSection>
      <FilterSection
        sectionKey="booleanName"
        icon="IconMathScientific"
        label="Person Name"
        activeCount={counts.booleanName || undefined}
        onClear={() => {
          updateFilter("boolean_expression", undefined);
          updateFilter("full_names", undefined);
          updateFilter("full_text_search", undefined);
        }}
      >
        <FilterBooleanNameSection />
      </FilterSection>
      <FilterSection
        sectionKey="network"
        icon="IconHandshake"
        label="Team network"
        activeCount={counts.network || undefined}
        onClear={() => updateFilter("network", undefined)}
      >
        <FilterNetworkSection />
      </FilterSection>
    </PoolCategoryGateProvider>
  );
}

export function FilterPanel({
  runSearch,
}: {
  /**
   * Run button props for hosts without a results provider (the hero): the
   * footer creates and runs a new search instead of re-running the draft.
   */
  runSearch?: RunSearchButtonProps;
} = {}) {
  const [presetSheetType, setPresetSheetType] = useState<PresetTypeSlug | null>(
    null,
  );
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveMode, setSaveMode] = useState<"new" | "existing" | null>(null);
  const { clearFilters, filters } = useTalentSearchFilters();
  const { filtersOpen, toggleFilters, resultsView } = useTalentSearch();
  const results = useOptionalTalentSearchResults();
  // Criteria live in the search's unsaved draft, so the pane only exists
  // inside a results provider (the hero has no draft to edit yet).
  const hasDraft = !!results;
  const [pane, setPane] = useState<FilterPanelPane>("filters");
  const criteriaCount = results?.draft.criteria?.length ?? 0;
  // Pin to 768, not the app-wide useIsMobile() default (1024): talent-search
  // keeps both panels in-flow side-by-side from md up (the provider's
  // exclusivity range is 768–1535), so only true mobile (<768) gets the drawer.
  const isMobile = useIsMobile(768);
  const collapsed = !filtersOpen;
  // Table view keeps the table at full width: the in-flow panel stays a rail
  // and the expanded filters float over the results (blurring what's behind)
  // instead of squeezing the columns.
  const overlayMode = !isMobile && resultsView === "table";

  const sectionCounts = useSectionCounts();
  const totalFilterCount = useMemo(
    () =>
      Object.values(sectionCounts).reduce((sum, n) => sum + n, 0) +
      splitLegacyClearanceHighlights(filters.highlight_types).highlights.length,
    [sectionCounts, filters.highlight_types],
  );

  // `expandedContent` is reused by both the desktop sidebar and the mobile
  // drawer — defining it once avoids re-rendering it under two parents with
  // different state. Both render paths wrap it in a <Filters.Container> so
  // the FiltersContext (used by <Filters.Header>'s collapse chevron) is
  // present in both.
  const expandedContent = useMemo(
    () => (
      <>
        <Filters.Header
          leading={
            pane === "criteria" && hasDraft ? (
              <div className="flex items-center gap-1.5">
                <div className="opacity-60">
                  <Icon
                    name="IconListSparkle"
                    size={18}
                    className="text-heading"
                  />
                </div>
                <span className="text-sm font-medium text-contrast/80">
                  Ranking criteria
                </span>
              </div>
            ) : undefined
          }
        />

        {hasDraft && (
          <div className="shrink-0 border-b border-border bg-contrast/1 px-3 py-2">
            <FilterPanelPaneSwitcher
              value={pane}
              onValueChange={setPane}
              counts={{ filters: totalFilterCount, criteria: criteriaCount }}
            />
          </div>
        )}

        {pane === "criteria" && hasDraft ? (
          // Criteria pane: the list scrolls in the body while Apply is
          // pinned in a footer, mirroring the Presets/Save row below.
          <SearchCriteriaPaneProvider>
            <Filters.Body>
              <SearchCriteriaPaneBody />
            </Filters.Body>
            <Filters.Footer variant="muted" className="flex h-12 items-center">
              <SearchCriteriaPaneFooter />
            </Filters.Footer>
          </SearchCriteriaPaneProvider>
        ) : (
        <>
        <Filters.Body>
          <FilterSectionsContent
            onOpenPreset={(type) => setPresetSheetType(type)}
          />
        </Filters.Body>

        <Filters.Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={clearFilters}
            className="relative h-7 min-h-7 w-full border border-contrast/30 bg-transparent shadow-none hover:border-contrast/50 hover:bg-transparent active:bg-transparent"
          >
            <span className="text-sm font-medium text-contrast/60 leading-5">
              Clear all filters
            </span>
          </Button>
        </Filters.Footer>

        <Filters.Footer variant="muted" className="h-12 flex items-center">
          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={() => setPresetSheetType("filters")}
              className="flex-1 flex items-center justify-center gap-1 h-7 px-1.5 rounded-md bg-contrast/3 border border-contrast/5 cursor-pointer hover:bg-contrast/6 transition-colors"
            >
              <div className="opacity-50">
                <Icon
                  name="IconLayoutDashboard"
                  size={16}
                  className="text-heading"
                />
              </div>
              <span className="text-sm font-medium text-heading leading-5 px-1">
                Presets
              </span>
            </button>
            <div className="flex-1 relative">
              <button
                type="button"
                onClick={() => setSaveMenuOpen((v) => !v)}
                className="w-full flex items-center justify-center gap-1 h-7 px-1.5 rounded-md bg-contrast/3 border border-contrast/5 cursor-pointer hover:bg-contrast/6 transition-colors"
              >
                <div className="opacity-50">
                  <Icon
                    name="IconShareArrowDown"
                    size={16}
                    className="text-heading"
                  />
                </div>
                <span className="text-sm font-medium text-heading leading-5 px-1">
                  Save
                </span>
              </button>
              {saveMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setSaveMenuOpen(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-1.5 w-56 bg-surface-elevated rounded-lg border border-border shadow-lg z-50 py-1 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setSaveMenuOpen(false);
                        setSaveMode("new");
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-heading hover:bg-contrast/4 cursor-pointer transition-colors"
                    >
                      <div className="opacity-60">
                        <Icon
                          name="IconPlusLarge"
                          size={16}
                          className="text-heading"
                        />
                      </div>
                      Save as new preset
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSaveMenuOpen(false);
                        setSaveMode("existing");
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-heading hover:bg-contrast/4 cursor-pointer transition-colors"
                    >
                      <div className="opacity-60">
                        <Icon
                          name="IconShareArrowDown"
                          size={16}
                          className="text-heading"
                        />
                      </div>
                      Update existing preset
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Filters.Footer>
        </>
        )}

        {/* Save is Run: the only way edits (filters or criteria) execute. */}
        <RunSearchFooter fallback={runSearch} />

        <ApplyPreset
          open={!!presetSheetType}
          onClose={() => setPresetSheetType(null)}
          presetType={presetSheetType ?? "filters"}
        />
        <CreatePreset
          open={saveMode === "new"}
          onClose={() => setSaveMode(null)}
          presetType="filters"
          initialFilters={filters}
        />
        {saveMode === "existing" && (
          <ExistingPresetPicker
            open
            onClose={() => setSaveMode(null)}
            filters={filters}
          />
        )}
      </>
    ),
    [
      presetSheetType,
      saveMenuOpen,
      saveMode,
      filters,
      clearFilters,
      hasDraft,
      pane,
      totalFilterCount,
      criteriaCount,
      runSearch,
    ],
  );

  // Toggle helper — base-ui-style controlled `onCollapsedChange(next)` mapped
  // onto the talent-search context's toggle (single-arg). The provider only
  // exposes a toggle, so we just fire it when the requested state differs.
  const handleCollapsedChange = useCallback(
    (next: boolean) => {
      if (next !== collapsed) toggleFilters();
    },
    [collapsed, toggleFilters],
  );

  return (
    <>
      {!isMobile && (
        <Filters.Container
          collapsed={overlayMode || collapsed}
          onCollapsedChange={handleCollapsedChange}
          expandedWidth={371}
          collapsedCount={totalFilterCount}
          data-tour="ts-filters"
        >
          {expandedContent}
        </Filters.Container>
      )}

      <AnimatePresence>
        {overlayMode && filtersOpen && (
          <>
            <motion.div
              key="table-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              // Above the table's sticky header (`z-40`) and sticky cells,
              // which otherwise poke through the dim.
              className="absolute inset-0 z-[60] bg-black/25 backdrop-blur-[3px] dark:bg-black/40"
              onClick={toggleFilters}
            />
            <motion.div
              key="table-panel"
              initial={{ x: -24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -24, opacity: 0 }}
              transition={{
                type: "spring",
                damping: 30,
                stiffness: 500,
                mass: 0.5,
              }}
              className="absolute inset-y-0 left-0 z-[70] flex w-[371px] flex-col border-r border-border bg-surface-primary shadow-[8px_0_32px_-8px_rgba(0,0,0,0.45)]"
            >
              <Filters.Container
                collapsed={false}
                onCollapsedChange={handleCollapsedChange}
                renderChildrenOnly
              >
                {expandedContent}
              </Filters.Container>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobile && filtersOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50"
              onClick={toggleFilters}
            />
            <motion.div
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              exit={{ x: "-100%" }}
              transition={{
                type: "spring",
                damping: 30,
                stiffness: 500,
                mass: 0.5,
              }}
              className="fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[371px] bg-surface-primary flex flex-col border-r border-border"
            >
              <Filters.Container
                collapsed={false}
                onCollapsedChange={handleCollapsedChange}
                renderChildrenOnly
              >
                {expandedContent}
              </Filters.Container>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Existing Preset Picker — pick a filter preset to overwrite          */
/* ------------------------------------------------------------------ */

interface PresetPickerResponse {
  id: string;
  title: string;
  preset_type: string;
  description?: string;
  visibility: string;
  created_at: string;
}

function ExistingPresetPicker({
  open,
  onClose,
  filters,
}: {
  open: boolean;
  onClose: () => void;
  filters: SearchFilters;
}) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{
    presets: PresetPickerResponse[];
    total: number;
  }>({
    queryKey: ["presets", "filters", "my", "save-picker"],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("preset_type", "filters");
      params.set("selector", "my");
      params.set("per_page", "50");
      return customInstance(`/api/presets?${params.toString()}`);
    },
    enabled: open,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, unknown>;
    }) =>
      customInstance(`/api/presets/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["presets"] });
      const preset = data?.presets.find((p) => p.id === variables.id);
      showToast.success(`Updated preset "${preset?.title ?? "Preset"}"`);
      onClose();
    },
    onError: () => {
      showToast.error("Failed to update preset");
    },
  });

  const handleSelect = useCallback(
    (preset: PresetPickerResponse) => {
      updateMutation.mutate({
        id: preset.id,
        payload: {
          filters_json: JSON.stringify(filters),
        },
      });
    },
    [filters, updateMutation],
  );

  const presets = data?.presets ?? [];

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent size="sm">
        <ModalHeader
          title="Update existing preset"
          iconName="IconShareArrowDown"
        />
        <ModalBody className="flex flex-col gap-1.5 max-h-[50vh]">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 rounded-lg bg-contrast/4 shimmer shimmer-bg text-foreground"
                />
              ))}
            </div>
          ) : presets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <p className="text-sm text-contrast/50">
                No filter presets found
              </p>
              <p className="text-xs text-contrast/30">
                Create a new preset instead
              </p>
            </div>
          ) : (
            presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelect(preset)}
                disabled={updateMutation.isPending}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-transparent hover:bg-contrast/4 hover:border-contrast/5 cursor-pointer transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="opacity-50 shrink-0">
                  <Icon
                    name="IconSettingsGear2"
                    size={16}
                    className="text-heading"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-heading truncate">
                    {preset.title}
                  </p>
                  {preset.description && (
                    <p className="text-xs text-contrast/50 truncate">
                      {preset.description}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <ModalClose asChild>
            <button
              type="button"
              className="bg-contrast/4 border border-border rounded-md h-7 px-3 text-sm font-medium text-heading hover:bg-contrast/6 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
            >
              Cancel
            </button>
          </ModalClose>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
